import { ATR, BollingerBands, EMA, MACD, RSI, SMA } from "technicalindicators";
import type { Candle } from "../types";

function toHLC(candles: Candle[]) {
  if (!Array.isArray(candles)) {
    return { high: [], low: [], close: [] };
  }
  return {
    high: candles.map((c) => c.high),
    low: candles.map((c) => c.low),
    close: candles.map((c) => c.close),
  };
}

function closes(candles: Candle[]): number[] {
  if (!Array.isArray(candles)) return [];
  return candles.map((c) => c.close);
}

export const ta = {
  /** Exponential Moving Average over closes */
  ema(candles: Candle[], period: number): number[] {
    return EMA.calculate({ values: closes(candles), period });
  },

  /** Simple Moving Average over closes */
  sma(candles: Candle[], period: number): number[] {
    return SMA.calculate({ values: closes(candles), period });
  },

  /** RSI over closes */
  rsi(candles: Candle[], period = 14): number[] {
    return RSI.calculate({ values: closes(candles), period });
  },

  /** ATR over high/low/close */
  atr(candles: Candle[], period = 14): number[] {
    const { high, low, close } = toHLC(candles);
    return ATR.calculate({ high, low, close, period });
  },

  /** MACD histogram array: {macd, signal, histogram} */
  macd(
    candles: Candle[],
    fast = 12,
    slow = 26,
    signalPeriod = 9
  ): Array<{ macd: number; signal: number; histogram: number }> {
    const result = MACD.calculate({
      values: closes(candles),
      fastPeriod: fast,
      slowPeriod: slow,
      signalPeriod,
      SimpleMAOscillator: false,
      SimpleMASignal: false,
    });
    return result
      .filter(
        (r): r is typeof r & { MACD: number; signal: number; histogram: number } =>
          r.MACD !== undefined && r.signal !== undefined && r.histogram !== undefined
      )
      .map((r) => ({ macd: r.MACD, signal: r.signal, histogram: r.histogram }));
  },

  /** Bollinger Bands over closes */
  bollinger(
    candles: Candle[],
    period = 20,
    stdDev = 2
  ): { upper: number[]; middle: number[]; lower: number[] } {
    const bands = BollingerBands.calculate({ values: closes(candles), period, stdDev });
    return {
      upper: bands.map((b) => b.upper),
      middle: bands.map((b) => b.middle),
      lower: bands.map((b) => b.lower),
    };
  },

  /** Last element of an indicator array (or null) */
  last<T>(arr: T[]): T | null {
    return arr.length > 0 ? arr[arr.length - 1] : null;
  },
};

export const taSafe = ta;
